import { Download, FileCode2, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteScript, downloadScriptExport, listScripts, ScriptSummary } from "../api/scripts";
import { LaunchJobModal } from "../components/LaunchJobModal";
import { relativeTime } from "../utils/format";

export function ScriptsPage(): JSX.Element {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState<ScriptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [launchScript, setLaunchScript] = useState<ScriptSummary | null>(null);

  async function reload() {
    try {
      setLoading(true);
      const items = await listScripts();
      setScripts(items);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function handleDelete(script: ScriptSummary) {
    if (!window.confirm(`Supprimer le script « ${script.name} » ? Cette action est irréversible.`)) {
      return;
    }
    try {
      await deleteScript(script.id);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Erreur de suppression");
    }
  }

  async function handleExport(script: ScriptSummary) {
    try {
      await downloadScriptExport(script.id, script.name);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Erreur d'export");
    }
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
            <FileCode2 className="size-6 text-velvet-400" />
            Scripts
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            DSL JSONC d'automatisation Velvet Finger. Éditeur Monaco avec autocomplete via JSON Schema.
          </p>
        </div>
        <button
          onClick={() => navigate("/scripts/new")}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium transition-colors"
        >
          <Plus className="size-4" />
          Nouveau script
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-ink-700 bg-ink-800/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-700/40 text-ink-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Nom</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Taille</th>
              <th className="text-left px-4 py-3">Modifié</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700/50">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-ink-500">
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && scripts.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-ink-500">
                  Aucun script. Créez-en un pour démarrer.
                </td>
              </tr>
            )}
            {!loading &&
              scripts.map((s) => (
                <tr key={s.id} className="hover:bg-ink-700/20 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/scripts/${s.id}`)}
                      className="text-white font-medium hover:text-velvet-300 transition-colors"
                    >
                      {s.name}
                    </button>
                    {s.description && (
                      <div className="text-xs text-ink-500 mt-0.5 line-clamp-1">{s.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-400">
                    <span className="px-2 py-0.5 rounded-md bg-ink-700/60 text-xs">{s.job_type}</span>
                  </td>
                  <td className="px-4 py-3 text-ink-400">{s.content_length} chars</td>
                  <td className="px-4 py-3 text-ink-400">{relativeTime(s.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setLaunchScript(s)}
                        title="Lancer un job"
                        className="p-2 rounded-md text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                      >
                        <Play className="size-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/scripts/${s.id}`)}
                        title="Éditer"
                        className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors"
                      >
                        <Pencil className="size-4" />
                      </button>
                      <button
                        onClick={() => void handleExport(s)}
                        title="Exporter .json"
                        className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors"
                      >
                        <Download className="size-4" />
                      </button>
                      <button
                        onClick={() => void handleDelete(s)}
                        title="Supprimer"
                        className="p-2 rounded-md text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {launchScript && (
        <LaunchJobModal
          script={launchScript}
          onClose={() => setLaunchScript(null)}
          onLaunched={(job) => {
            setLaunchScript(null);
            navigate(`/jobs/${job.id}`);
          }}
        />
      )}
    </div>
  );
}
